/**
 * EditPostPage - Edit existing post caption and tags
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { usePostPermissions } from '@/hooks/usePostPermissions';
import { Post } from '@/types';
import { ArrowLeft, Save, X } from 'lucide-react';
import { toast } from 'sonner';

export default function EditPostPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { postId } = useParams<{ postId: string }>();
  const { firebaseUser } = useAuth();
  const permissions = usePostPermissions();

  const [post, setPost] = useState<Post | null>(null);
  const [caption, setCaption] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadPost();
  }, [postId]);

  const loadPost = async () => {
    if (!postId) {
      toast.error(t('feed.invalidPostId'));
      navigate('/feed');
      return;
    }

    try {
      setLoading(true);
      const postDoc = await getDoc(doc(db, 'posts', postId));

      if (!postDoc.exists()) {
        toast.error(t('feed.postNotFound'));
        navigate('/feed');
        return;
      }

      const postData = { postId: postDoc.id, ...postDoc.data() } as Post;

      // Check permission
      if (!canEditPost(postData)) {
        toast.error(t('feed.noPermission'));
        navigate('/feed');
        return;
      }

      setPost(postData);
      setCaption(postData.caption || '');
      setTags(postData.tags || []);
    } catch (error) {
      console.error('Error loading post:', error);
      toast.error(t('feed.loadError'));
      navigate('/feed');
    } finally {
      setLoading(false);
    }
  };

  const canEditPost = (post: Post): boolean => {
    if (!firebaseUser || permissions.loading) return false;

    // Author can edit their own post (if they have canEdit permission)
    if (post.authorId === firebaseUser.uid && permissions.canEdit) return true;

    // Users with canEditAny permission can edit any post
    if (permissions.canEditAny) return true;

    return false;
  };

  const handleAddTag = () => {
    const tag = tagInput.trim().toLowerCase().replace(/^#/, '');

    if (!tag) return;

    if (tags.includes(tag)) {
      toast.error(t('feed.tagExists'));
      return;
    }

    if (tags.length >= 10) {
      toast.error(t('feed.maxTags'));
      return;
    }

    setTags([...tags, tag]);
    setTagInput('');
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(t => t !== tagToRemove));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!postId) return;

    try {
      setSaving(true);

      const updatePostFunc = httpsCallable(functions, 'updatePost');
      await updatePostFunc({
        postId,
        updates: {
          caption: caption.trim(),
          tags,
        },
      });

      toast.success(t('feed.postUpdated'));
      navigate('/feed');
    } catch (error: any) {
      console.error('Error updating post:', error);
      toast.error(error.message || t('feed.publishError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-2 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-info/30 mx-auto mb-4"></div>
          <p className="text-ink-muted">{t('feed.loadingPost')}</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return null;
  }

  return (
    <div className="min-h-screen bg-surface-2 py-8">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate('/feed')}
            className="flex items-center gap-2 text-ink-muted hover:text-ink dark:hover:text-white transition"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{t('feed.backToFeed')}</span>
          </button>
          <h1 className="text-2xl font-bold text-ink">
            {t('feed.editPostTitle')}
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Post Preview */}
          <div className="bg-surface rounded-lg shadow-sm overflow-hidden">
            <div className="p-4">
              <p className="text-sm font-semibold text-ink-subtle mb-2">{t('feed.preview')}</p>
            </div>
            <img
              src={post.imageUrl}
              alt="Post preview"
              className="w-full aspect-square object-cover"
            />
          </div>

          {/* Caption */}
          <div className="bg-surface rounded-lg shadow-sm p-6">
            <label className="block text-sm font-medium text-ink-muted mb-2">
              {t('feed.description')}
            </label>
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder={t('feed.writeDescription')}
              rows={4}
              maxLength={500}
              className="w-full px-4 py-2 border border-hairline bg-surface text-ink rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent resize-none"
            />
            <p className="text-xs text-ink-subtle mt-2">
              {caption.length}/500 {t('feed.characters')}
            </p>
          </div>

          {/* Tags */}
          <div className="bg-surface rounded-lg shadow-sm p-6">
            <label className="block text-sm font-medium text-ink-muted mb-2">
              {t('feed.tagsMax')}
            </label>

            {/* Tag Input */}
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
                placeholder={t('feed.tagInputPlaceholder')}
                maxLength={30}
                className="flex-1 px-4 py-2 border border-hairline bg-surface text-ink rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent"
              />
              <button
                type="button"
                onClick={handleAddTag}
                disabled={!tagInput.trim() || tags.length >= 10}
                className="px-4 py-2 bg-info text-white rounded-lg hover:bg-info/90 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                {t('feed.add')}
              </button>
            </div>

            {/* Tag Chips */}
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <div
                    key={tag}
                    className="flex items-center gap-2 px-3 py-1 bg-info/10 text-info rounded-full text-sm"
                  >
                    <span>#{tag}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="hover:text-info transition"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {tags.length === 0 && (
              <p className="text-sm text-ink-subtle italic">
                {t('feed.noTags')}
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate('/feed')}
              className="flex-1 px-6 py-3 border border-hairline text-ink-muted rounded-lg hover:bg-surface-2 transition"
            >
              {t('feed.cancel')}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-info text-white rounded-lg hover:bg-info/90 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>{t('feed.saving')}</span>
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  <span>{t('feed.saveChanges')}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
